import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchBreakdown, type AdminBreakdown } from '@/lib/adapters'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'

// A16.07 — versiya taqsimoti (adapters.ts ga tegmaslik uchun modul-lokal fetch).
// Backend: GET /admin/analytics/versions (`handlers/admin.go` AnalyticsVersions).
interface VersionDatum {
  version: string
  users: number
}
interface VersionBreakdownData {
  by_app_version: VersionDatum[]
  by_os_version: VersionDatum[]
}
function fetchVersionBreakdown(signal?: AbortSignal): Promise<VersionBreakdownData> {
  return api.get<VersionBreakdownData>('/admin/analytics/versions', signal ? { signal } : undefined)
}

// A16.37 — top botlar (faol foydalanuvchi bo'yicha). Modul-lokal.
// Backend: GET /admin/analytics/top-bots (`handlers/admin.go` AnalyticsTopBots).
interface TopBotDatum {
  username: string
  active_users: number
  has_mini_app: boolean
  business: boolean
}
async function fetchTopBots(signal?: AbortSignal): Promise<TopBotDatum[]> {
  return (
    await api.get<{ bots: TopBotDatum[] }>(
      '/admin/analytics/top-bots',
      signal ? { signal } : undefined,
    )
  ).bots
}

// A16.09 — til bo'yicha taqsimot (modul-lokal fetch; adapters.ts ga tegmaslik uchun).
// Backend: GET /admin/analytics/languages (`handlers/admin.go` AnalyticsLanguages).
interface LanguageDatum {
  lang: string
  users: number
}
async function fetchLanguages(signal?: AbortSignal): Promise<LanguageDatum[]> {
  return (
    await api.get<{ by_language: LanguageDatum[] }>(
      '/admin/analytics/languages',
      signal ? { signal } : undefined,
    )
  ).by_language
}

// A16.05 — hafta-kun×soat faollik matritsasi ([7][24], satr 0=Dushanba). Modul-lokal.
// Backend: GET /admin/analytics/activity-matrix (`handlers/admin.go` AnalyticsActivityMatrix).
async function fetchActivityMatrix(signal?: AbortSignal): Promise<number[][]> {
  return (
    await api.get<{ matrix: number[][] }>(
      '/admin/analytics/activity-matrix',
      signal ? { signal } : undefined,
    )
  ).matrix
}

/** Umumiy: breakdown'ni bir marta yuklab, render-callback'ga uzatadi. */
function useBreakdown() {
  const [data, setData] = useState<AdminBreakdown | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBreakdown(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  return { data, loading, error, retry: () => setReload((n) => n + 1) }
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** A16.06 — Platforma bo'yicha. */
export function PlatformBreakdown() {
  const t = useT()
  const { data, loading, error, retry } = useBreakdown()
  const bars = useMemo<BarDatum[]>(
    () => (data?.by_platform ?? []).map((p) => ({ label: p.platform, value: p.users })),
    [data],
  )
  return (
    <>
      <PageHeader title={t.analytics.byPlatform} screenId="A16.06" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={retry} />
        ) : (
          <Panel title={t.analytics.byPlatform}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
            )}
          </Panel>
        )}
      </div>
    </>
  )
}

/** A16.11 — Chat turlari bo'yicha. */
export function ChatTypeBreakdown() {
  const t = useT()
  const { data, loading, error, retry } = useBreakdown()
  const bars = useMemo<BarDatum[]>(
    () => (data?.by_chat_type ?? []).map((c) => ({ label: c.type, value: c.count })),
    [data],
  )
  return (
    <>
      <PageHeader title={t.analytics.byChatType} screenId="A16.11" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={retry} />
        ) : (
          <Panel title={t.analytics.byChatType}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
            )}
          </Panel>
        )}
      </div>
    </>
  )
}

/** A16.07 — Versiya bo'yicha (ilova + OS versiyasi). */
export function VersionBreakdown() {
  const t = useT()
  const [data, setData] = useState<VersionBreakdownData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchVersionBreakdown(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const appBars = useMemo<BarDatum[]>(
    () => (data?.by_app_version ?? []).map((v) => ({ label: v.version, value: v.users })),
    [data],
  )
  const osBars = useMemo<BarDatum[]>(
    () => (data?.by_os_version ?? []).map((v) => ({ label: v.version, value: v.users })),
    [data],
  )
  return (
    <>
      <PageHeader title={t.analytics.byVersion} screenId="A16.07" />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <>
            <Panel title={t.analytics.byAppVersion}>
              {loading ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <BarChart data={appBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
              )}
            </Panel>
            <Panel title={t.analytics.byOsVersion}>
              {loading ? (
                <Skeleton className="h-40 w-full" />
              ) : (
                <BarChart data={osBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
              )}
            </Panel>
          </>
        )}
      </div>
    </>
  )
}

/** A16.37 — Top botlar (faol foydalanuvchi soni bo'yicha). */
export function TopBots() {
  const t = useT()
  const [data, setData] = useState<TopBotDatum[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchTopBots(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const bots = data ?? []
  return (
    <>
      <PageHeader title={t.analytics.topBots} screenId="A16.36" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <Panel title={t.analytics.topBots}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : !bots.length ? (
              <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
            ) : (
              <ol className="space-y-1">
                {bots.map((b, i) => (
                  <li
                    key={b.username || i}
                    className="flex items-center gap-3 rounded-md border border-border-subtle bg-surface-1 px-3 py-2 text-sm"
                  >
                    <span className="w-6 shrink-0 text-right font-mono text-xs text-tertiary">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-primary">
                      {b.username ? `@${b.username}` : '—'}
                    </span>
                    {b.has_mini_app && (
                      <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-secondary">
                        Mini App
                      </span>
                    )}
                    {b.business && (
                      <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-secondary">
                        Business
                      </span>
                    )}
                    <span className="shrink-0 font-mono tabular text-primary">
                      {b.active_users.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        )}
      </div>
    </>
  )
}

/** A16.09 — Til bo'yicha (interfeys tili taqsimoti, user_settings.lang). */
export function ByLanguage() {
  const t = useT()
  const [data, setData] = useState<LanguageDatum[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchLanguages(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const bars = useMemo<BarDatum[]>(
    () => (data ?? []).map((l) => ({ label: l.lang.toUpperCase(), value: l.users })),
    [data],
  )
  return (
    <>
      <PageHeader title={t.analytics.byLanguage} screenId="A16.09" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <Panel title={t.analytics.byLanguage}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <BarChart data={bars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
            )}
          </Panel>
        )}
      </div>
    </>
  )
}

/** A16.05 — Faollik heatmap (hafta-kun × soat, oxirgi 30 kun; xabar faolligi). */
export function ActivityHeatmap() {
  const t = useT()
  const [matrix, setMatrix] = useState<number[][] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchActivityMatrix(ac.signal)
      .then((m) => {
        if (alive) setMatrix(m)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])
  const rows = matrix ?? []
  const max = rows.reduce((mx, r) => Math.max(mx, ...r), 0)
  const hasData = rows.some((r) => r.some((c) => c > 0))
  const days = t.analytics.weekdays
  return (
    <>
      <PageHeader title={t.analytics.activity} screenId="A16.05" description={t.analytics.activityNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : (
          <Panel title={t.analytics.activity}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : !hasData ? (
              <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[560px] space-y-1">
                  <div className="flex gap-1 pl-8">
                    {Array.from({ length: 24 }, (_, h) => (
                      <div key={h} className="flex-1 text-center font-mono text-[9px] text-tertiary">
                        {h % 3 === 0 ? h : ''}
                      </div>
                    ))}
                  </div>
                  {rows.map((row, d) => (
                    <div key={d} className="flex items-center gap-1">
                      <div className="w-7 shrink-0 font-mono text-[10px] text-tertiary">{days[d] ?? d}</div>
                      <div className="flex flex-1 gap-1">
                        {row.map((c, h) => {
                          const intensity = max ? c / max : 0
                          return (
                            <div
                              key={h}
                              className="h-6 flex-1 rounded-sm"
                              style={{
                                backgroundColor: `color-mix(in oklab, var(--color-accent) ${Math.round(intensity * 100)}%, var(--surface-3))`,
                              }}
                              title={`${days[d] ?? d} ${h}:00 — ${c.toLocaleString()}`}
                            />
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        )}
      </div>
    </>
  )
}
